import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc, collectionGroup, Firestore } from "firebase/firestore";
import { db } from "../firebase/firebase";

function serializeFirestoreData(data: unknown): unknown {
  if (data && typeof data === 'object') {
    const serialized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      const val = value as { toDate?: () => Date };
      if (value && typeof value === 'object' && val.toDate && typeof val.toDate === 'function') {
        serialized[key] = val.toDate().toISOString();
      } else if (Array.isArray(value)) {
        serialized[key] = value.map(item => serializeFirestoreData(item));
      } else if (value && typeof value === 'object') {
        serialized[key] = serializeFirestoreData(value);
      } else {
        serialized[key] = value;
      }
    }

    return serialized;
  }

  return data;
}

async function fetchTags(): Promise<unknown[]> {
  const ref = collection(db, "tabbed_dict");
  const querySnapshot = await getDocs(ref);
  const tags: unknown[] = [];
  querySnapshot.forEach((docSnap) => {
    tags.push({
      id: docSnap.id,
      path: `tabbed_dict/${docSnap.id}`,
      collection: 'tabbed_dict',
      subcollection: null,
      ...(serializeFirestoreData(docSnap.data()) as object)
    });
  });
  console.log("Fetched tags:", tags);
  return tags;
}

async function searchTabbedDict({ searchTerm, isAdmin }: { searchTerm?: string; isAdmin?: boolean } = {}): Promise<unknown[]> {
  const ref = collection(db, "tabbed_dict");
  const querySnapshot = await getDocs(ref);
  const results: unknown[] = [];
  querySnapshot.forEach((docSnap) => {
    results.push({
      id: docSnap.id,
      path: `tabbed_dict/${docSnap.id}`,
      collection: 'main',
      subcollection: null,
      ...(serializeFirestoreData(docSnap.data()) as object)
    });
  });
  console.log("Search results:", results);
  return results;
}

async function newSearchTabbedDict({ searchTerm, isAdmin }: { searchTerm?: string; isAdmin?: boolean } = {}): Promise<unknown[]> {
  const ref = collection(db, "tabbed_dict");
  const querySnapshot = await getDocs(ref);
  const results: unknown[] = [];
  querySnapshot.forEach((docSnap) => {
    results.push({
      id: docSnap.id,
      path: `tabbed_dict/${docSnap.id}`,
      collection: 'main',
      subcollection: null,
      ...(serializeFirestoreData(docSnap.data()) as object)
    });
  });
  return results;
}

interface SearchResultItem {
  id?: string;
  collection?: string;
  subcollection?: string;
  parentDocId?: string;
  path?: string;
  title?: string;
  description?: string;
  content?: string;
  [key: string]: unknown;
}

async function searchTabbedDictWithSubcollections({ searchTerm, isAdmin }: { searchTerm?: string; isAdmin?: boolean } = {}): Promise<SearchResultItem[]> {
  try {
    let results: SearchResultItem[] = [];

    const mainRef = collection(db, "tabbed_dict");
    const mainSnapshot = await getDocs(mainRef);

    mainSnapshot.forEach((docSnap) => {
      results.push({
        id: docSnap.id,
        collection: 'main',
        subcollection: null,
        path: `tabbed_dict/${docSnap.id}`,
        ...(docSnap.data() as object)
      });
    });

    const subcollectionGroupQuery = collectionGroup(db, "data");
    const subcollectionSnapshot = await getDocs(subcollectionGroupQuery);

    subcollectionSnapshot.forEach((docSnap) => {
      const pathParts = docSnap.ref.path.split("/");
      const parentDocId = pathParts[1];
      const subcollectionName = pathParts[2];
      const subDocId = pathParts[3];

      results.push({
        id: subDocId,
        collection: 'subcollection',
        subcollection: subcollectionName,
        parentDocId: parentDocId,
        path: docSnap.ref.path,
        ...(serializeFirestoreData(docSnap.data()) as object)
      });
    });

    if (searchTerm && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      results = results.filter(item =>
        (item.id || '').toLowerCase().includes(searchLower) ||
        (item.title || '').toLowerCase().includes(searchLower) ||
        (item.description || '').toLowerCase().includes(searchLower) ||
        (item.content || '').toLowerCase().includes(searchLower) ||
        (item.subcollection || '').toLowerCase().includes(searchLower) ||
        (item.collection || '').toLowerCase().includes(searchLower) ||
        (item.parentDocId || '').toLowerCase().includes(searchLower)
      );
    }

    results.sort((a, b) => {
      if (a.collection === 'main' && b.collection === 'subcollection') return -1;
      if (a.collection === 'subcollection' && b.collection === 'main') return 1;

      if (a.collection === 'subcollection' && b.collection === 'subcollection') {
        const parentCompare = (a.parentDocId || '').localeCompare(b.parentDocId || '');
        if (parentCompare !== 0) return parentCompare;
      }

      return (a.id || '').localeCompare(b.id || '');
    });

    return results;
  } catch (error) {
    console.error("Error in searchTabbedDictWithSubcollections:", error);
    throw error;
  }
}

async function deleteDocWithSubcollections(dbInstance: Firestore, path: string, subcollectionName?: string): Promise<void> {
  const pathParts = path.split('/').filter(Boolean) as [string, ...string[]];
  if (!subcollectionName) {
    const parentRef = doc(dbInstance, pathParts[0], ...pathParts.slice(1));
    await deleteDoc(parentRef);
    return;
  }

  const parentRef = doc(dbInstance, pathParts[0], ...pathParts.slice(1));
  const subcollections = await getDocs(collection(parentRef, subcollectionName));

  if (subcollections.size > 0) {
    for (const subDoc of subcollections.docs) {
      await deleteDoc(subDoc.ref);
    }
  }

  await deleteDoc(parentRef);
}

export const tabbedDictApi = createApi({
  reducerPath: "tabbedDictApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["TabbedDict"],
  endpoints: (builder) => ({
    getTags: builder.query({
      async queryFn() {
        try {
          const tags = await fetchTags();
          return { data: tags };
        } catch (error) {
          console.error((error as Error).message);
          return { error: (error as Error).message };
        }
      },
      providesTags: ["TabbedDict"],
    }),
    searchTabbedDict: builder.query({
      async queryFn({ searchTerm, isAdmin }: { searchTerm?: string; isAdmin?: boolean } = {}) {
        try {
          const results = await searchTabbedDict({ searchTerm, isAdmin });
          return { data: results };
        } catch (error) {
          console.error((error as Error).message);
          return { error: (error as Error).message };
        }
      },
      providesTags: ["TabbedDict"],
    }),
    newSearchTabbedDict: builder.query({
      async queryFn({ searchTerm, isAdmin }: { searchTerm?: string; isAdmin?: boolean } = {}) {
        try {
          const results = await newSearchTabbedDict({ searchTerm, isAdmin });
          return { data: results };
        } catch (error) {
          console.error((error as Error).message);
          return { error: (error as Error).message };
        }
      },
      providesTags: ["TabbedDict"],
    }),
    searchTabbedDictWithSubcollections: builder.query({
      async queryFn({ searchTerm, isAdmin }: { searchTerm?: string; isAdmin?: boolean } = {}) {
        try {
          const results = await searchTabbedDictWithSubcollections({ searchTerm, isAdmin });
          return { data: results };
        } catch (error) {
          console.error((error as Error).message);
          return { error: (error as Error).message };
        }
      },
      providesTags: ["TabbedDict"],
    }),
    createTabbedDict: builder.mutation({
      async queryFn({ id, data, subcollection, datetimeCreated, datetimeUpdated, content }: {
        id: string;
        data: Record<string, unknown>;
        subcollection: string;
        datetimeCreated?: string;
        datetimeUpdated?: string;
        content?: unknown;
      }) {
        const docRef = doc(db, 'tabbed_dict', subcollection, id, 'data');
        try {
          await setDoc(docRef, { ...data, datetimeCreated, datetimeUpdated});
          return { data: { id, ...data } };
        } catch (error) {
          console.error('Error creating document:', error);
          return { error: (error as Error).message };
        }
      },
      invalidatesTags: ["TabbedDict"],
    }),
    updateTabbedDict: builder.mutation({
      async queryFn({ id, data, subcollection, datetimeUpdated, content, type = 'main' }: {
        id: string;
        data: Record<string, unknown>;
        subcollection?: string;
        datetimeUpdated?: string;
        content?: unknown;
        type?: string;
      }) {
        try {
          let docRef;
          
          if (type === 'main') {
            docRef = doc(db, 'tabbed_dict', id);
          } else if (type === 'user') {
            docRef = doc(db, 'tabbed_dict', subcollection!, id);
          } else if (type === 'userData') {
            docRef = doc(db, 'tabbed_dict', subcollection!, id, 'data');
          } else {
            docRef = doc(db, 'tabbed_dict', id);
          }
          
          await updateDoc(docRef, {...data, datetimeUpdated, content});
          return { data: { id, ...data, datetimeUpdated, content } };
        } catch (error) {
          console.error('Error updating document:', error);
          return { error: (error as Error).message };
        }
      },
      invalidatesTags: ["TabbedDict"],
    }),
    deleteTabbedDict: builder.mutation({
      async queryFn({ id, subcollection, path, subcollectionName }: { id?: string; subcollection?: string; path?: string; subcollectionName?: string } = {}) {
        try {
          const docPath = path || `tabbed_dict/${subcollection}/${id}`;
          await deleteDocWithSubcollections(db, docPath, subcollectionName);
          return { data: { id, path: docPath } };
        } catch (error) {
          console.error('Error deleting document:', error);
          return { error: (error as Error).message };
        }
      },
      invalidatesTags: ["TabbedDict"],
    }),
  }),
});

if (typeof window !== 'undefined') {
  (window as Window & { fetchTagsQuery?: typeof fetchTags }).fetchTagsQuery = fetchTags;
  (window as Window & { searchTabbedDictQuery?: typeof searchTabbedDict }).searchTabbedDictQuery = searchTabbedDict;
  (window as Window & { newSearchTabbedDictQuery?: typeof newSearchTabbedDict }).newSearchTabbedDictQuery = newSearchTabbedDict;
  (window as Window & { searchTabbedDictWithSubcollectionsQuery?: typeof searchTabbedDictWithSubcollections }).searchTabbedDictWithSubcollectionsQuery = searchTabbedDictWithSubcollections;

  (window as Window & { createTabbedDict?: (params: { id: string; data: object; subcollection?: string }) => Promise<unknown> }).createTabbedDict = async ({ id, data, subcollection = 'public' }) => {
    try {
      const docRef = doc(db, 'tabbed_dict', subcollection, id);
      await setDoc(docRef, data);
      console.log(`✅ Created document: ${subcollection}/${id}`, { id, ...data });
      return { data: { id, ...data } };
    } catch (error) {
      console.error('❌ Error creating document:', error);
      return { error: (error as Error).message };
    }
  };

  (window as Window & { updateTabbedDict?: (params: { id: string; data: object; subcollection?: string }) => Promise<unknown> }).updateTabbedDict = async ({ id, data, subcollection = 'public' }) => {
    try {
      const docRef = doc(db, 'tabbed_dict', subcollection, id);
      await updateDoc(docRef, data);
      console.log(`✅ Updated document: ${subcollection}/${id}`, { id, ...data });
      return { data: { id, ...data } };
    } catch (error) {
      console.error('❌ Error updating document:', error);
      return { error: (error as Error).message };
    }
  };

  (window as Window & { deleteTabbedDict?: (params?: { id?: string; subcollection?: string; path?: string; subcollectionName?: string }) => Promise<unknown> }).deleteTabbedDict = async ({ id, subcollection = 'public', path, subcollectionName } = {}) => {
    try {
      const docPath = path || `tabbed_dict/${subcollection}/${id}`;
      await deleteDocWithSubcollections(db, docPath, subcollectionName);
      console.log(`✅ Deleted document: ${docPath}`);
      return { data: { id, path: docPath } };
    } catch (error) {
      console.error('❌ Error deleting document:', error);
      return { error: (error as Error).message };
    }
  };
}

export const {
  useGetTagsQuery,
  useSearchTabbedDictQuery,
  useNewSearchTabbedDictQuery,
  useSearchTabbedDictWithSubcollectionsQuery,
  useCreateTabbedDictMutation,
  useUpdateTabbedDictMutation,
  useDeleteTabbedDictMutation
} = tabbedDictApi;
